import { useCallback, useEffect, useRef, useState } from 'react';

const GUARD_MS = 500;
const DISARM_MS = 3500;

/**
 * Two-step "Clear all" confirmation (ui_specs: destructive clinical actions
 * need one). First activation arms, second executes `onClear`; the armed
 * state resets when the list empties by other means. A second activation
 * within GUARD_MS of arming is ignored, so a double-click or a held key
 * cannot confirm; left alone, the armed state resets after DISARM_MS.
 */
export function useConfirmClear(
    itemCount: number,
    onClear: () => void
): { confirming: boolean; requestClear: () => void } {
    const [confirming, setConfirming] = useState(false);
    const armedAt = useRef(0);

    // Drop the pending confirmation if the list empties by other means.
    useEffect(() => {
        if (itemCount === 0) {
            setConfirming(false);
        }
    }, [itemCount]);

    useEffect(() => {
        if (confirming) {
            const timer = setTimeout(() => setConfirming(false), DISARM_MS);
            return () => clearTimeout(timer);
        }
    }, [confirming]);

    const requestClear = useCallback(() => {
        if (confirming) {
            if (performance.now() - armedAt.current < GUARD_MS) {
                return;
            }
            onClear();
            setConfirming(false);
        } else {
            armedAt.current = performance.now();
            setConfirming(true);
        }
    }, [confirming, onClear]);

    return { confirming, requestClear };
}
